/**
 * PDP v2 delivery rows, cart-aware (019 M13; Mat, 2026-09-30). shipping-info
 * renders the rates and "Free over $X" unchanged: the baseline without script,
 * which never over-promises. <pdp-v2-delivery> is the only writer of the
 * cart-aware status. It works on the bag AFTER adding this item: the bag total
 * and thresholds from the cart drawer's free-shipping bar, the offer lines
 * from the drawer's own Collection Offer state island (the same render, so
 * the two always agree), and this item's price through the offer maths.
 * Each tier (standard, then express) reads:
 *   already  "Free · {transit}"   "Your bag is already over {threshold}"
 *   with     "Free · {transit}"   "With this item, your bag is over {threshold}"
 *   short    "{price} · {transit}" "Free over {threshold} · add {gap} more"
 * Nothing is claimed without a projection: no bar, or an offer product whose
 * state island is missing or invalid (044-F1). A tier is free only when the
 * projected bag clears it, even if today's bag does (044-F2). With no variant
 * chosen, the lowest projected bag across the candidates is used (044-F3).
 * It never promises more than the bar will show after the add; codes typed
 * at checkout are unknowable (they count before the threshold).
 * The island's offer counts only while Collection Offer state lists its
 * slot as live (002-F1): once the slot has ended the rows stay and project a
 * plain add; a slot this page's offer config never carried (content from a
 * newer render, soft-navigated in) cannot be projected, so no claim.
 */
(function () {
  'use strict';

  var theme = window.theme = window.theme || {};
  if (theme.pdpV2Delivery) return;
  var D = theme.pdpV2Delivery = {};

  /**
   * The bag total after adding one unit of item, in cents. bag already holds
   * today's discounts, so an offer's existing saving is subtracted only when
   * this add newly qualifies the bag. Without an offer (or CO), plain add.
   */
  D.project = function (bag, item, offer, co, lines) {
    if (item.giftCard) return bag;
    var price = Number(item.price) || 0;
    if (!offer || !co) return bag + price;
    var slot = Number(offer.slot);
    var base = (lines || []).slice();
    var before = co.computeState([offer], base)[slot];
    var after = co.computeState([offer], base.concat([{ slot: slot, quantity: 1, price: price, compareAt: Number(item.compareAt) || 0 }]))[slot];
    if (!after || !after.qualified) return bag + price;
    return bag + price - co.unitSaving(price, item.compareAt, offer.percentage) - (before.qualified ? 0 : before.existingSaving);
  };

  /** The lowest projected bag across candidate items (044-F3), or null. */
  D.projectMin = function (bag, items, offer, co, lines) {
    var best = null;
    (items || []).forEach(function (item) {
      var p = D.project(bag, item, offer, co, lines);
      if (best === null || p < best) best = p;
    });
    return best;
  };

  /** 002-F1: the offer's slot is listed live by the Collection Offer module (CO.getOffers). */
  D.isLive = function (offer, co) {
    var slot = Number(offer && offer.slot);
    return !!(co && typeof co.getOffers === 'function' && co.getOffers().some(function (o) { return Number(o.slot) === slot; }));
  };

  /** 002-F1: the offer's slot has ended on this page (the class layout/theme.liquid sets at its end). */
  D.ended = function (offer, doc) {
    var html = doc && doc.documentElement;
    return !!(html && html.classList && html.classList.contains('co-ended-' + Number(offer && offer.slot)));
  };

  /** One tier's state, judged on the bag after the add; null keeps the baseline. */
  D.tier = function (threshold, bag, projected) {
    if (!(threshold > 0) || !isFinite(bag) || projected === null || !isFinite(projected)) return null;
    if (projected >= threshold) return { state: bag >= threshold ? 'already' : 'with', threshold: threshold };
    return { state: 'short', threshold: threshold, gap: threshold - projected };
  };

  function money(cents, whole) {
    var opts = whole && cents % 100 === 0 ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {};
    if (typeof theme.formatMoney === 'function') return theme.formatMoney(cents, opts);
    return '$' + (whole && cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2));
  }
  D.money = money;

  /** The copy for a tier; the baseline texts when state is null. */
  D.copy = function (t, base, words) {
    if (!t) return { free: false, value: base.value, note: base.note };
    var transit = base.value.split('·').pop().trim();
    var over = money(t.threshold, true);
    if (t.state === 'already') return { free: true, value: words.free + ' · ' + transit, note: 'Your bag is already over ' + over };
    if (t.state === 'with') return { free: true, value: words.free + ' · ' + transit, note: 'With this item, your bag is over ' + over };
    return { free: false, value: base.value, note: base.note + ' · add ' + money(t.gap) + ' more' };
  };

  /**
   * The offer state stamped in the cart drawer (snippets/collection-offer-state-json),
   * validated by the offer module: {wholesale, lines}, or null when the island
   * is missing or invalid. Read on every update, never cached (044-F1).
   */
  D.offerState = function (doc, co) {
    var el = doc.querySelector('[data-collection-offer-state]');
    if (!el || !co || typeof co.normaliseState !== 'function') return null;
    try { return co.normaliseState(JSON.parse(el.textContent || '')); } catch (e) { return null; }
  };

  function readIsland(root) {
    var el = root.querySelector('script[data-pdp-v2-delivery]');
    try { return el ? JSON.parse(el.textContent) : null; } catch (e) { return null; }
  }

  var EVENTS = ['on:cart:after-merge', 'on:collection-offer:state'];

  class PdpV2Delivery extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this.config = readIsland(this);
      if (!this.config) return;
      this._on = true;
      // Standard, then express: shipping-info's line order, as the bar's tiers.
      this.rows = Array.prototype.slice.call(this.querySelectorAll('.shipping-info__line'), 0, 2).map(function (li) {
        var value = li.querySelector('.shipping-info__value');
        var note = li.querySelector('.shipping-info__free');
        if (!value || !note) return null;
        // A restored snapshot keeps painted text: the baseline lives on the element.
        if (!li.hasAttribute('data-base-value')) {
          li.setAttribute('data-base-value', value.textContent.trim());
          li.setAttribute('data-base-note', note.textContent.trim());
        }
        return { li: li, value: value, note: note, base: { value: li.getAttribute('data-base-value'), note: li.getAttribute('data-base-note') } };
      });
      this.form = this.closest('product-form');
      this._update = this.update.bind(this);
      EVENTS.forEach(function (t) { document.addEventListener(t, this._update); }, this);
      if (this.form) this.form.addEventListener('on:variant:change', this._update);
      this.update();
    }

    disconnectedCallback() {
      if (!this._on) return;
      this._on = false;
      EVENTS.forEach(function (t) { document.removeEventListener(t, this._update); }, this);
      if (this.form) this.form.removeEventListener('on:variant:change', this._update);
    }

    /** The chosen variant; else every available one (else every one). */
    items() {
      var c = this.config;
      var giftCard = !!c.giftCard;
      var input = this.form && this.form.querySelector('buy-buttons [name="id"]');
      var chosen = input && c.variants[input.value];
      var all = chosen ? [chosen] : Object.keys(c.variants).map(function (id) { return c.variants[id]; });
      var pool = chosen ? all : all.filter(function (v) { return v.available !== false; });
      return (pool.length ? pool : all).map(function (v) { return { price: v.price, compareAt: v.compareAt, giftCard: giftCard }; });
    }

    update() {
      var bar = document.querySelector('.free-shipping-bar[data-free-shipping-current-cart]');
      var bag = bar ? Number(bar.getAttribute('data-free-shipping-current-cart')) : NaN;
      var thresholds = bar ? [Number(bar.getAttribute('data-free-shipping-amount')), Number(bar.getAttribute('data-free-shipping-express-amount'))] : [];
      var co = theme.collectionOffers;
      var offer = this.config.offer;
      var live = !!offer && D.isLive(offer, co);
      var projected = null;
      if (isFinite(bag)) {
        if (!offer || (!live && D.ended(offer, document))) {
          projected = D.projectMin(bag, this.items(), null);
        } else if (live) {
          var state = D.offerState(document, co);
          if (state) projected = D.projectMin(bag, this.items(), state.wholesale ? null : offer, co, state.lines);
        }
      }
      var words = { free: this.config.free || 'Free' };
      this.rows.forEach(function (row, i) {
        if (!row) return;
        var copy = D.copy(D.tier(thresholds[i], bag, projected), row.base, words);
        row.li.classList.toggle('pdp-v2-free', copy.free);
        if (row.value.textContent !== copy.value) row.value.textContent = copy.value;
        if (row.note.textContent !== copy.note) row.note.textContent = copy.note;
      });
    }
  }

  if (!customElements.get('pdp-v2-delivery')) customElements.define('pdp-v2-delivery', PdpV2Delivery);
})();
